/**
 * DemandBoard - Sprint 7 Demand Management
 * Kanban board view with columns per status
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Plus, Filter, RefreshCw, List, ArrowRight,
  Clock, TrendingUp, AlertTriangle, Database, Trash2, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLUMN_CONFIG = [
  { id: 'intake', label: 'Intake', color: 'blue', nextStates: ['assessment', 'rejected'] },
  { id: 'assessment', label: 'Assessment', color: 'yellow', nextStates: ['governance', 'intake', 'rejected'] },
  { id: 'governance', label: 'Governance', color: 'purple', nextStates: ['approved', 'rejected', 'assessment'] },
  { id: 'approved', label: 'Approved', color: 'green', nextStates: [] },
  { id: 'rejected', label: 'Rejected', color: 'red', nextStates: ['intake'] }
];

const COLUMN_COLORS = {
  blue: 'border-t-blue-500',
  yellow: 'border-t-yellow-500',
  purple: 'border-t-purple-500',
  green: 'border-t-green-500',
  red: 'border-t-red-500'
};

const DemandCard = ({ demand, onTransition, onView }) => {
  const [showActions, setShowActions] = useState(false);
  const column = COLUMN_CONFIG.find(c => c.id === demand.status) || {};

  return (
    <div
      className="bg-white border rounded-lg p-3 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
      onClick={() => onView(demand.id)}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
      data-testid={`demand-card-${demand.id}`}
    >
      <div className="flex justify-between items-start mb-2">
        <h4 className="font-medium text-sm line-clamp-2">{demand.title}</h4>
        <Badge className={`text-xs ${
          demand.score >= 7 ? 'bg-green-100 text-green-700' :
          demand.score >= 4 ? 'bg-yellow-100 text-yellow-700' :
          'bg-red-100 text-red-700'
        }`}>
          {demand.score?.toFixed(1)}
        </Badge>
      </div>

      <div className="flex flex-wrap gap-1 mb-2">
        <Badge className="text-xs bg-gray-100 text-gray-600">{demand.department}</Badge>
        {demand.category && (
          <Badge className="text-xs bg-gray-100 text-gray-600">{demand.category}</Badge>
        )}
        {demand.opex_flag && (
          <Badge className="text-xs bg-orange-100 text-orange-700">OPEX</Badge>
        )}
      </div>

      <div className="flex items-center gap-3 text-xs text-gray-500">
        <div className="flex items-center gap-1">
          <Clock className="h-3 w-3" />
          {demand.age_days}d
        </div>
        <div className="flex items-center gap-1">
          <TrendingUp className="h-3 w-3" />
          {demand.impact}
        </div>
        <div className="flex items-center gap-1">
          <AlertTriangle className="h-3 w-3" />
          {demand.urgency}
        </div>
      </div>

      {/* Quick Actions */}
      {showActions && column.nextStates?.length > 0 && (
        <div 
          className="mt-2 pt-2 border-t flex flex-wrap gap-1"
          onClick={(e) => e.stopPropagation()}
        >
          {column.nextStates.map(nextState => (
            <Button
              key={nextState}
              size="sm"
              variant="ghost"
              className="h-6 text-xs"
              onClick={() => onTransition(demand.id, nextState)}
            >
              <ArrowRight className="h-3 w-3 mr-1" />
              {nextState}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
};

const DemandBoard = () => {
  const navigate = useNavigate();
  const { user, hasRole } = useAuth();
  const [columns, setColumns] = useState({});
  const [loading, setLoading] = useState(true);
  const [demoLoading, setDemoLoading] = useState(false);
  const [hasDemoData, setHasDemoData] = useState(false);
  const [filters, setFilters] = useState({
    department: '',
    category: '',
    opex: '',
    minScore: '',
    maxScore: ''
  });

  const isAdmin = hasRole('admin', 'sysadmin', 'owner', 'pmo_power');

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('simpliview_token');
    // TENANT ISOLATION: Include X-Tenant-ID header for tenant context
    const tenantId = localStorage.getItem('impersonated_tenant_id') || localStorage.getItem('simpliview_user_tenant_id');
    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
    if (tenantId) {
      headers['X-Tenant-ID'] = tenantId;
    }
    return headers;
  }, []);

  // Check demo data status
  const checkDemoStatus = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/v2/demands/demo/status`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setHasDemoData(data.has_demo_data);
      }
    } catch (err) {
      console.error('Failed to check demo status:', err);
    }
  }, [getAuthHeaders]);

  // Load demo data
  const handleLoadDemoData = async () => {
    if (!window.confirm('Load demo demands? This will add 16 sample demands to demonstrate the system.')) return;
    
    setDemoLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/v2/demands/demo/seed`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Loaded ${data.demands?.length || 0} demo demands`);
        setHasDemoData(true);
        fetchBoard();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to load demo data');
      }
    } catch (err) {
      console.error('Failed to load demo data:', err);
      toast.error('Failed to load demo data');
    } finally {
      setDemoLoading(false);
    }
  };

  // Reset demo data
  const handleResetDemoData = async () => {
    if (!window.confirm('Reset demo data? This will remove all demo demands from the system.')) return;
    
    setDemoLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/v2/demands/demo/reset`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Removed ${data.deleted?.demands || 0} demo demands`);
        setHasDemoData(false);
        fetchBoard();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to reset demo data');
      }
    } catch (err) {
      console.error('Failed to reset demo data:', err);
      toast.error('Failed to reset demo data');
    } finally {
      setDemoLoading(false);
    }
  };

  const fetchBoard = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.department) params.append('department', filters.department);
      if (filters.category) params.append('category', filters.category);
      if (filters.opex !== '') params.append('opex', filters.opex);
      if (filters.minScore) params.append('min_score', filters.minScore);
      if (filters.maxScore) params.append('max_score', filters.maxScore);

      const response = await fetch(`${API_URL}/api/v2/demands/board?${params}`, {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        setColumns(data.columns || {});
      }
    } catch (err) {
      console.error('Failed to fetch board:', err);
      toast.error('Failed to load demand board');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, filters]);

  useEffect(() => {
    fetchBoard();
    if (isAdmin) {
      checkDemoStatus();
    }
  }, [fetchBoard, isAdmin, checkDemoStatus]);

  const handleTransition = async (demandId, toStatus) => {
    try {
      const response = await fetch(`${API_URL}/api/v2/demands/${demandId}/transition`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ to: toStatus })
      });

      if (response.ok) {
        toast.success(`Demand moved to ${toStatus}`);
        fetchBoard();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Transition failed');
      }
    } catch (err) {
      console.error('Transition failed:', err);
      toast.error('Failed to transition demand');
    }
  };

  const handleView = (demandId) => {
    navigate(`/demands/${demandId}`);
  };

  return (
    <div className="p-6 space-y-4" data-testid="demand-board">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Demand Board</h1>
          <p className="text-gray-500">Visual pipeline of demands through governance stages</p>
        </div>
        <div className="flex gap-2">
          {/* Demo Data Buttons - Admin Only */}
          {isAdmin && (
            <>
              {!hasDemoData ? (
                <Button 
                  variant="outline" 
                  onClick={handleLoadDemoData} 
                  disabled={demoLoading}
                  data-testid="load-demo-data-btn"
                  className="border-purple-300 text-purple-700 hover:bg-purple-50"
                >
                  {demoLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Database className="h-4 w-4 mr-2" />}
                  Load Demo Data
                </Button>
              ) : (
                <Button 
                  variant="outline" 
                  onClick={handleResetDemoData} 
                  disabled={demoLoading}
                  data-testid="reset-demo-data-btn"
                  className="border-red-300 text-red-700 hover:bg-red-50"
                >
                  {demoLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                  Reset Demo Data
                </Button>
              )}
            </>
          )}
          <Button variant="outline" onClick={() => navigate('/demands')} data-testid="view-list-btn">
            <List className="h-4 w-4 mr-2" />
            List View
          </Button>
          <Button onClick={() => navigate('/demands/new')} data-testid="create-demand-btn">
            <Plus className="h-4 w-4 mr-2" />
            New Demand
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="py-3">
          <div className="flex flex-wrap items-center gap-3">
            <Filter className="h-4 w-4 text-gray-500" />

            <input
              type="text"
              placeholder="Department"
              value={filters.department}
              onChange={(e) => setFilters({...filters, department: e.target.value})}
              className="px-3 py-1.5 text-sm border rounded-md w-32"
            />

            <input
              type="text"
              placeholder="Category"
              value={filters.category}
              onChange={(e) => setFilters({...filters, category: e.target.value})}
              className="px-3 py-1.5 text-sm border rounded-md w-32"
            />

            <select
              value={filters.opex}
              onChange={(e) => setFilters({...filters, opex: e.target.value})}
              className="px-3 py-1.5 text-sm border rounded-md bg-white"
            >
              <option value="">All Types</option>
              <option value="true">OPEX Only</option>
              <option value="false">CAPEX Only</option>
            </select>

            <div className="flex items-center gap-1">
              <input
                type="number"
                placeholder="Min Score"
                value={filters.minScore}
                onChange={(e) => setFilters({...filters, minScore: e.target.value})}
                className="px-2 py-1.5 text-sm border rounded-md w-20"
              />
              <span>-</span>
              <input
                type="number"
                placeholder="Max Score"
                value={filters.maxScore}
                onChange={(e) => setFilters({...filters, maxScore: e.target.value})}
                className="px-2 py-1.5 text-sm border rounded-md w-20"
              />
            </div>

            <Button variant="ghost" size="sm" onClick={fetchBoard}>
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Board */}
      <div className="grid grid-cols-5 gap-4 min-h-[600px]" data-testid="board-columns">
        {COLUMN_CONFIG.map(column => {
          const columnData = columns[column.id] || { cards: [], count: 0 };
          
          return (
            <div key={column.id} className="flex flex-col">
              <Card className={`border-t-4 ${COLUMN_COLORS[column.color]}`}>
                <CardHeader className="py-3 px-4">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-medium">{column.label}</CardTitle>
                    <Badge variant="outline" className="text-xs">
                      {columnData.count}
                    </Badge>
                  </div>
                </CardHeader>
              </Card>

              <div className="flex-1 space-y-2 mt-2 overflow-y-auto max-h-[calc(100vh-320px)]">
                {loading ? (
                  <div className="flex items-center justify-center py-8 text-gray-400">
                    <RefreshCw className="h-5 w-5 animate-spin" />
                  </div>
                ) : columnData.cards.length === 0 ? (
                  <div className="text-center py-8 text-gray-400 text-sm">
                    No demands
                  </div>
                ) : (
                  columnData.cards.map(demand => (
                    <DemandCard
                      key={demand.id}
                      demand={{...demand, status: column.id}}
                      onTransition={handleTransition}
                      onView={handleView}
                    />
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DemandBoard;
